import { getCache, setCache, setSnackbar } from '@/helpers';
import {
    accessDepartment,
    createCompany,
    getCompany,
    getMe,
    registerProfile,
    registerUser,
    getClientData,
} from '../../Api/ApiUser';

const initialState = () => ({
    error: {
        status: false,
        title: '',
        msg: '',
    },
    connected: false,
    activeStep: 0,
    activeStepView: 'INITIAL',
    hasAccount: false,
    stepTitle: 'Välkommen till Falagrett!',
    stepText:
        'Det enkla och säkra sättet att ha kontroll på intyg och certifikat. Kom igång snabbt, tryggt och gratis. Skapa ditt konto på några minuter.',
    register: {
        first_name: '',
        last_name: '',
        national_identity_number: '',
        email: '',
        phone_number: '',
    },
    company: {
        department_name: '',
        name: '',
        id: null,
        organization_number: null,
        departments: [],
        name_disabled: true,
        department_disabled: true,
        department_info: true,
        type: '',
        plan_id: null,
        industry_id: null,
        country_id: null,
        invited_by: null,
        email: null,
        phone_number: null,
    },
    departments: [],
    bankId: null,
});

const state = initialState();

const mutations = {
    SET_BANK_ID(state, obj) {
        state.bankId = {
            orderRef: obj.orderRef,
            autoStartToken: obj.autoStartToken,
        };
    },
    SET_HINT_AND_STATUS_CODE(state, obj) {
        state.bankId.status = obj.status;
        state.bankId.hintCode = obj.hintCode;
    },
    SET_USER(state, user) {
        state.register.first_name = user.first_name;
        state.register.last_name = user.last_name;
        state.register.national_identity_number = user.national_identity_number;
        state.register.email = user.email;
        state.register.accessDepartments = user?.departments?.filter((d) => d.department_status === 'accepted') || [];
    },
    STATE_UPDATE_REGISTER(state, field) {
        const key = Object.keys(field)[0];
        state.register[key] = field[key];
    },
    STATE_UPDATE_COMPANY(state, field) {
        const key = Object.keys(field)[0];
        state.company[key] = field[key];
    },
    SET_STARTING_STEP(state) {
        state.activeStep = 1;
    },
    SET_UPDATE_STEP(state, step) {
        if (step === 2) {
            state.stepTitle = 'Snart klar!';
        }

        if (step === 3) {
            state.stepTitle = '';
            state.stepText = '';
        }

        state.activeStep = step;
    },
    SET_UPDATE_STEP_VIEW(state, step) {
        state.activeStepView = step;
    },
    SET_COMPANY(state, obj) {
        state.company.id = obj.id;
        state.company.name = obj.name;
        state.company.organization_number = obj.organization_number;
        state.company.country_id = obj.country_id ?? state.company.country_id;
        state.company.plan_id = obj.plan_id;
        state.company.type = obj.type;
        state.company.departments = obj.departments;
        state.company.department_disabled = false;

        if (state.company.name === '') {
            state.company.name_disabled = false;
        }
    },
    SET_INVITED_COMPANY(state, obj) {
        state.company.name = obj.name;
        state.company.organization_number = obj.organization_number;
        state.company.departments = obj.departments;
        state.company.department_disabled = false;
        state.company.country_id = obj.country;
        state.company.invited_by = obj.invited_by;

        if (state.company.name === '') {
            state.company.name_disabled = false;
        }
        state.company.email = obj.email;
        state.company.phone_number = obj.phone_number;
    },
    SET_EMPLOY_COMPANY(state, obj) {
        state.company.id = obj.id;
        state.company.name = obj.name;
        state.company.organization_number = obj.organization_number;
        state.company.plan_id = obj.plan_id;
        state.company.type = obj.type;

        if (state.company.name === '') {
            state.company.name_disabled = false;
        }
    },

    SET_CONNECTED_DEPARTMENT(state, id) {
        // Create new copy of state
        const departments = [...state.company.departments];
        // Find the current we need to change
        const current = departments.find((dep) => dep.id === id);
        // Do the actually changes
        current.connected_department = true;

        state.company.department_info = false;
        state.connected = true;
        // Assign the new copy
        state.company.departments = departments;
    },
    SET_COMPNAY_DISABLE(state, bool) {
        state.company.name_disabled = bool;
    },
    CLEAR_COMPANY(state) {
        state.company.industry_id = null;
        state.company.id = null;
        state.company.name = '';
        state.company.departments = [];
        state.company.department_disabled = true;
        state.company.name_disabled = true;
    },
    SET_HAS_ACCOUNT(state, bool) {
        state.hasAccount = bool;
    },
    RESET_BANKID(state) {
        state.bankId = null;
    },
    RESET_STATE: (state) => Object.assign(state, initialState()),
};

const actions = {
    async REGISTER_USER({ state, commit, rootState, dispatch }, payload = {}) {
        return await registerUser({
            first_name: state.register.first_name,
            last_name: state.register.last_name,
            email: state.register.email,
            national_identity_number: state.register.national_identity_number,
            phone_number: state.register.phone_number,
            ...payload,
        }).then((response) => {
            if (response.status === 'Error') {
                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                commit('GlobalStore/SET_LOADING', false, { root: true });
                return false;
            }

            const cache = getCache();

            if (rootState.UserStore.user.incomplete_user !== null) {
                /**
                 * The user has been invited and therefor as an incomplete user
                 * Remove incomplete flag and get the user
                 * AcceptDepartmentInvite.vue watches on the User and will automatically accept the invite
                 */

                commit('UserStore/SET_INCOMPLETE_USER', null, { root: true });
                dispatch('UserStore/GET_ME', null, { root: true });
            } else if (cache && cache.token) {
                /**
                 * The user is registering through the login page
                 */

                commit('SET_UPDATE_STEP', 2);
                commit('SET_UPDATE_STEP_VIEW', 'CREATING');
            }

            return true;
        });
    },
    async REGISTER_PROFILE({ dispatch }) {
        const payload = {
            email: state.register.email,
            phone_number: state.register.phone_number,
        };

        return await registerProfile(payload).then((response) => {
            if (response.status === 'Success') {
                return response.data;
            }

            if (response.status === 'Error') {
                dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
            }
        });
    },
    async GET_ME({ commit }) {
        await getMe().then((response) => {
            if (response.status === 'Success') {
                commit('SET_USER', response.data);
            }
        });
    },
    async REQUEST_COMPANY({ commit, state }) {
        return await getCompany({ organization_number: state.company.organization_number }).then((response) => {
            if (response.data !== null) {
                commit('SET_COMPANY', response.data);
            } else {
                commit('SET_COMPNAY_DISABLE', false);
            }
        });
    },
    async REQUEST_INVITED_CLIENT({ commit }, invite_id) {
        return await getClientData({ invite_id: invite_id }).then((response) => {
            if (response && response.data !== null) {
                commit('SET_INVITED_COMPANY', response.data);
                if (response.data.name) {
                    return true;
                }
            } else {
                commit('CLEAR_COMPANY');
                commit('SET_COMPNAY_DISABLE', false);
            }
            return false;
        });
    },
    async REQUEST_ACCESS_DEPARTMENT({ commit }, department_id) {
        return await accessDepartment({ department_id: department_id }).then((response) => {
            if (response.status === 'Success') {
                commit('SET_CONNECTED_DEPARTMENT', department_id);
            }

            return response.status === 'Success';
        });
    },
    async REQUEST_CREATE_COMPNAY({ state, dispatch }) {
        let payload = {
            company: {
                name: state.company.name,
                organization_number: state.company.organization_number,
            },
            type: state.company.type,
            name: state.company.department_name,
            plan_id: state.company.plan_id,
            industry_id: state.company.industry_id,
            country_id: state.company.country_id,
            invited_by: state.company.invited_by,
        };

        if (state.company.id) {
            payload.company.id = state.company.id;
        }

        if (!state.company.departments || state.company.departments.length === 0 || !state.connected) {
            return await createCompany(payload).then((response) => {
                if (response.status === 'Success') {
                    const cache = getCache();

                    cache.department_id = response.data.department.id;
                    cache.currentDepartment = response.data.department;
                    cache.current_profile_id = response.data.user_profile.id;
                    cache.current_profile = response.data.user_profile;

                    setCache(cache);
                }

                if (response.status === 'Error') {
                    dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                }

                return response.status === 'Success';
            });
        }
    },
    async REQUEST_CREATE_EMPLOY_COMPANY({ state, dispatch }) {
        let payload = {
            company: {
                name: state.company.name,
                organization_number: state.company.organization_number,
            },
            billing_email: state.company.billing_email,
            billing_reference: state.company.billing_reference,
            billing_address: state.company.billing_address,
            billing_city: state.company.billing_city,
            billing_zipcode: state.company.billing_zipcode,
            billing_phone: state.company.billing_phone,
            type: state.company.type,
            plan_id: state.company.plan_id,
            industry_id: state.company.industry_id,
            country_id: state.company.country_id,
            invited_by: state.company.invited_by,
        };

        if (state.company.id) {
            payload.company.id = state.company.id;
        }

        if (!state.company.departments || state.company.departments.length === 0 || !state.connected) {
            return await createCompany(payload).then((response) => {
                if (response.status === 'Success') {
                    const cache = getCache();

                    cache.department_id = response.data.department.id;
                    cache.currentDepartment = response.data.department;
                    cache.current_profile_id = response.data.user_profile.id;
                    cache.current_profile = response.data.user_profile;

                    setCache(cache);
                }

                if (response.status === 'Error') {
                    dispatch('GlobalStore/SET_SNACKBAR', setSnackbar(response), { root: true });
                }

                return response.status === 'Success';
            });
        }
    },
};

const getters = {
    GET_BANKID: (state) => state.bankId,
    GET_REGISTER_USER: (state) => state.register,
    GET_COMPANY: (state) => state.company,
    isConnected: (state) => state.connected,
    GET_STEP: (state) => state.activeStep,
    GET_HAS_ACCOUNT: (state) => state.hasAccount,
    getApproved: (state) => state.register.approved,
    getStepTitle: (state) => state.stepTitle,
    getStepText: (state) => state.stepText,
    GET_STEP_VIEW: (state) => state.activeStepView,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
